// Polyfill mínimo de Node Buffer para el browser. Necesario para `@react-pdf/renderer`
// v4 (su `fetchImage` interno usa `Buffer.isBuffer` y otras APIs Node que no existen
// nativamente en navegadores). Vite no incluye polyfills automáticos como Webpack.
//
// Usamos solo el package `buffer` (no `vite-plugin-node-polyfills`) porque ese plugin
// reportaba crashes con react-pdf por incompatibilidad con zlib/stream — ver
// https://github.com/diegomura/react-pdf/issues/2454. Polyfilling SOLO Buffer evita
// interferir con los shims internos de react-pdf para el resto de módulos Node.
import { Buffer } from 'buffer';
import { Capacitor } from '@capacitor/core';
(window as unknown as { Buffer: typeof Buffer }).Buffer = Buffer;
(globalThis as unknown as { Buffer: typeof Buffer }).Buffer = Buffer;

import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { excludeGraphQLFetch } from 'apollo-link-sentry';
import { name, version } from '../package.json';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: `${name}@${version}`,
  integrations: [
    // V7, no V6: `react-router` es 7.18.2. La V6 estaba puesta desde antes
    // de la migración y nadie la cambió al saltar de versión.
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    // ── Session Replay: RETIRADO (2026-08-15) ──────────────────────────────
    //
    // Medido en tablet con perfil de CPU del WebView por CDP: rrweb
    // —`addEvent` → `_sendEventToWorker` → `postMessage`, más 949 ms de
    // `removeEventListener`— era lo que hacía que los modales de croquis,
    // galería y cámara tardaran una eternidad en montarse. Un A/B instalando
    // dos APKs que solo diferían en esto lo confirmó.
    //
    // 🔴 No costaba el 10% que sugiere `replaysSessionSampleRate`: con
    // `replaysOnErrorSampleRate` distinto de 0 el SDK entra en MODO BÚFER y
    // graba CONTINUAMENTE en memoria (anillo de 60 s), suba o no suba. El
    // coste de CPU se pagaba en el 100% de las sesiones. La ÚNICA forma de
    // dejar de grabar es no registrar la integración (o poner los dos
    // muestreos a 0), y por eso esto no se arregla bajando el porcentaje.
    //
    // No se gatea por plataforma: la web móvil corre en la MISMA CPU de la
    // tablet, así que un gate por `isNativePlatform()` dejaba el problema
    // intacto justo donde duele.
    //
    // Qué se pierde: el vídeo. Qué se conserva: errores con traza,
    // breadcrumbs (clics, navegaciones, red, consola) y las trazas de
    // rendimiento — que es de donde ha salido siempre el diagnóstico útil en
    // este proyecto. El propio Sentry tiene abierto
    // https://github.com/getsentry/sentry/issues/83352 (crashes en Android de
    // gama baja) y https://github.com/getsentry/sentry/issues/84055 (poder
    // apagar el replay conservando el resto del panel).
    //
    // Además estaba en la configuración más cara y menos privada posible
    // (`maskAllText: false`, `blockAllMedia: false`), que Sentry reserva para
    // sitios estáticos SIN datos sensibles: esto maneja nombres de operarios,
    // matrículas, partes de accidente y fotos.
    //
    // Si algún día se recupera, que sea con enmascarado puesto y midiendo
    // antes en la tablet.

    // Logs estructurados a Sentry: SOLO warn/error de consola (no info/debug).
    Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
  ],
  beforeBreadcrumb: excludeGraphQLFetch,
  environment: import.meta.env.MODE,
  // 🔴 A dónde se manda la cabecera `sentry-trace`, que es lo que permite que
  // una traza del navegador CONTINÚE en el backend.
  //
  // Las anteriores NO casaban con la API. La regex era
  // `^https://roisplatform\.com` y la API vive en `api.roisplatform.com`; el
  // subdominio no encajaba, así que la cabecera nunca salía hacia el backend y
  // el `tracesSampler` de abajo —que respeta `parentSampled`— no tenía nada que
  // respetar: no llegaba ningún padre.
  //
  // Peor: `roisplatform.com` a secas es la WEB DE CAPTACIÓN (rois-web), no la
  // app. Se propagaba al único sitio que no hace falta.
  //
  // El subdominio opcional cubre `api.`, `api-beta.`, `app.` y `beta.`. El
  // `(\/|$)` del final ancla el host: sin él,
  // `https://roisplatform.com.malicioso.com` también casaría y estaríamos
  // filtrando cabeceras de traza a un dominio ajeno.
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/([a-z0-9-]+\.)?roisplatform\.com(\/|$)/,
  ],
  // Performance Monitoring — P1.5 muestreo por operación (antes 1.0 = 100% quemaba cuota):
  // - transacciones `critical:*` (guardados envueltos con traceCritical, PENDIENTE de
  //   save-entity) → 100%, y su decisión viaja al backend vía sentry-trace (traza FE→BE).
  // - navegaciones de ruta / lecturas → 5%.
  // Hoy nada emite `critical:*` aún (la rama está lista); el envolver los guardados se hace
  // con save-entity (cadena no-atómica + cola offline → demasiado invasivo de instrumentar hoy).
  tracesSampler: (samplingContext) => {
    const name = samplingContext.name;
    if (typeof name === 'string' && name.startsWith('critical:')) return 1.0;
    if (typeof samplingContext.parentSampled === 'boolean') {
      return samplingContext.parentSampled;
    }
    return 0.05;
  },
  // Session Replay retirado — ver el comentario de `integrations`. Los dos a
  // 0 por coherencia: aunque sin la integración no se graba nada, dejar el
  // 0.1 aquí haría creer que sigue activo al 10%.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  // Logs estructurados (cubo aparte; 5 GB/mes en Team).
  enableLogs: true,
  // Multi-tenant: NO enviar PII por defecto (IP, headers). Antes estaba en true.
  sendDefaultPii: false,
  // Sin `beforeSend`: NO se filtra ningún error. Hubo aquí un filtro de "errores
  // benignos" y se retiró por tres razones:
  //
  // 1. Dos de sus patrones (`ChunkLoadError`, `Loading chunk N failed`) son de
  //    webpack y esto es Vite: no podían dispararse nunca.
  // 2. `AbortError` miraba el campo equivocado — vive en `exception.values[0].type`,
  //    no en el mensaje — y además el único `AbortController` de la app
  //    (`apiEndpointManager`) ya captura el abort, así que no llega a Sentry.
  // 3. El único patrón vivo, `Failed to fetch dynamically imported module`, NO es
  //    benigno en esta app: es PWA + APK usados sin cobertura, y ese error suele
  //    significar que el chunk de una ruta no se precacheó (mismo fallo que tuvimos
  //    con las traducciones). El plan de code splitting multiplicará esa superficie.
  //
  // La cuota de errores nunca fue el problema del front (lo eran los replays, los
  // spans y el spam de ora-back), así que filtrar aquí costaba señal sin ahorrar
  // nada. Si algún deploy provoca una ráfaga, se añade un filtro CON datos que
  // distinga el deploy rancio del hueco de precache.
});

// Initialize i18n
import './i18n';

// Register PWA Service Worker for offline support.
//
// Usamos `registerType: 'prompt'` en vite.config.js — el SW nuevo se queda
// en estado `waiting` hasta que el usuario confirme nuestro modal. Aquí
// conectamos los callbacks de vite-plugin-pwa con el versionManager:
//
// - `onNeedRefresh`: el SW detectó una versión nueva → forzamos la
//   verificación del versionManager para que muestre el modal único de
//   actualización (compartido con el flujo de polling al backend).
// - El `updateSW` devuelto por `registerSW` se registra en el versionManager
//   para que su `onContinue` lo invoque y active el SW v2 antes del reload.
import { registerSW } from 'virtual:pwa-register';
import {
  versionManager,
  VersionManager,
  VERSION_MODAL_RECENT_FIRE_KEY,
  VERSION_MODAL_RECENT_FIRE_TTL_MS,
} from './utils/versionManager';
import { CHECK_UPDATE_EVENT, isWebUpdateEnabled } from './utils/webVersion';

// El WebView de Capacitor NO expone Service Workers, así que este bloque
// entero es código muerto dentro de la app: el modal de "versión nueva", el
// dedup del bug workbox#2526, `setSWUpdater`... nada de eso llega a
// ejecutarse. Se gatea explícitamente para que quede DICHO —y para que
// `onRegisterError` no mande un error por dispositivo a Sentry si algún día
// una versión del WebView empieza a exponer el API a medias.
//
// Actualizar el bundle dentro de la app es trabajo del OTA (Fase 3), no del
// Service Worker.
// 🔴 Y en la app nativa NO basta con no registrarlo: hay que RETIRAR el que
// pudiera haber quedado.
//
// El origen del WebView es `https://localhost` y su almacenamiento sobrevive a
// la reinstalación del APK. Un service worker registrado por cualquier build
// anterior —una versión previa a este gate, o un build con recarga en vivo
// apuntando al dev server— sigue vivo ahí y **sigue sirviendo lo que tenga
// precacheado**, por encima de los assets que trae el APK.
//
// El síntoma es de los peores: la app parece actualizada —el APK es nuevo, los
// métodos nativos nuevos responden— pero el JavaScript que corre es el viejo.
// Verificado en una Tab Active5 el 2026-09-01: el depurador del WebView listaba
// un contexto `service_worker · https://localhost/sw.js` en un APK cuyo código
// hace un año que no registra ninguno.
//
// Se retira el registro y se vacían las cachés HTTP que dejó. NO toca
// IndexedDB ni OPFS: la sesión, las colas offline, la traza GPS y los croquis
// viven ahí y no se rozan.
const ORPHAN_SW_CLEANED_KEY = 'rois:orphan-sw-cleaned';

if (Capacitor.isNativePlatform() && 'serviceWorker' in navigator) {
  void (async () => {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      // El caso normal: no hay ninguno y esto no hace nada más.
      if (registrations.length === 0) return;

      await Promise.all(registrations.map((r) => r.unregister()));

      // Las cachés se vacían DESPUÉS de retirar el registro, y antes de
      // recargar. Contienen copias de ficheros que el APK ya trae.
      if (typeof caches !== 'undefined') {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map((k) => caches.delete(k)));
      }

      Sentry.captureMessage(
        '[PWA] service worker huérfano retirado dentro del APK',
        {
          level: 'warning',
          tags: { feature: 'pwa', platform: 'native' },
          extra: { registrations: registrations.length },
        }
      );

      // 🔴 Hay que RECARGAR, y por un motivo concreto: hasta que la página se
      // recargue sigue CONTROLADA por el service worker que acabamos de
      // retirar. La app carga por trozos, así que si el operario navega a una
      // pantalla cuyo trozo no está cargado todavía, ese service worker pedirá
      // el fichero con el nombre VIEJO —el del bundle que tenía precacheado— y
      // en el APK nuevo ese fichero no existe: pantalla rota a mitad de sesión.
      //
      // Recargar cuesta menos de un segundo, la sesión lo sobrevive
      // (`sessionStorage`) y deja la app arrancada limpiamente desde el APK.
      //
      // La marca impide el bucle: si por lo que fuera la limpieza no acabara de
      // surtir efecto, se recarga UNA vez y no más.
      if (!sessionStorage.getItem(ORPHAN_SW_CLEANED_KEY)) {
        sessionStorage.setItem(ORPHAN_SW_CLEANED_KEY, '1');
        window.location.reload();
      }
    } catch {
      // No se bloquea nada por esto: si no se puede limpiar, la app arranca
      // igual — solo seguiría con el riesgo de servir un bundle viejo.
    }
  })();
}

if (!Capacitor.isNativePlatform() && 'serviceWorker' in navigator) {
  // El aviso de actualización en web lo abre UN solo sitio desde el
  // 2026-10-01: `useAppUpdate`, con la misma política que en la app (spec
  // 2026-09-30). Su fuente es `/version.json` del propio dominio. Aquí el SW
  // solo PIDE una comprobación inmediata cuando detecta una versión nueva en
  // `waiting` (evento `CHECK_UPDATE_EVENT`); el arranque en frío lo resuelve
  // `versionManager.initialize()` en silencio y el sondeo al backend está
  // apagado (App.tsx).
  //
  // Gate: el mismo que el aviso (`isWebUpdateEnabled`: producción, o el
  // interruptor de pruebas de las E2E).

  // Flag a nivel de módulo para deduplicar `onNeedRefresh` dentro de la
  // MISMA sesión de página. Bug confirmado en Chrome (GoogleChrome/workbox#2526):
  // tras un reload Chrome dispara DOS eventos `waiting` distintos —
  // uno con `wasWaitingBeforeRegister=true` y otro con `isUpdate=true`.
  // vite-pwa traduce AMBOS en `onNeedRefresh`, así que sin este flag el
  // segundo evento abre un modal espurio aunque el sentinel sessionStorage
  // ya lo haya suprimido y vaciado.
  let onNeedRefreshFired = false;

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      if (!isWebUpdateEnabled()) return;

      // Defensa contra DOBLE MODAL tras race del reload + bug Chrome.
      //
      // Escenario A (race del reload, fix original PR #37):
      //   Usuario pulsa Continuar → `onContinue` en versionManager envía
      //   SKIP_WAITING y programa `reload(500ms)`. Si SW v2 no termina de
      //   activar en esa ventana (CPU/red del usuario), el reload se
      //   produce mientras SW v1 aún controla la página → el navegador
      //   sirve el bundle VIEJO desde la caché de v1. Cuando el bundle
      //   viejo vuelve a registrar workbox-window, workbox detecta SW v2
      //   en `waiting` y dispara su evento `waiting` con
      //   `wasWaitingBeforeRegister:true` → vite-pwa → onNeedRefresh →
      //   segundo modal.
      //
      // Escenario B (bug Chrome del DOBLE waiting event):
      //   GoogleChrome/workbox#2526 — Chrome dispara DOS eventos waiting
      //   tras un reload sin skipWaiting completo: uno con
      //   wasWaitingBeforeRegister y otro con isUpdate. vite-pwa los
      //   traduce a DOS `onNeedRefresh` consecutivos. Sin el flag de
      //   módulo de abajo, el primero se suprime por el sentinel y borra
      //   el sentinel; el segundo encuentra sentinel vacío y abre modal.
      //
      // Defensa #1 (flag de módulo): mismo page session — el segundo
      //   evento se suprime aunque el sentinel ya esté gone.
      // Defensa #2 (sentinel sessionStorage): cross-reload — si el SW
      //   sigue racing en la siguiente carga, también suprime.
      //   IMPORTANTE: NO borramos el sentinel en la supresión (regresión
      //   del fix anterior). Dejamos que el TTL de 30s lo invalide de
      //   forma natural, así sobrevive a múltiples `waiting` events que
      //   Chrome puede disparar en milisegundos consecutivos.
      //
      // Layer 1 del fix (en versionManager.onContinue) espera a SW v2 a
      // `activated` antes del reload, eliminando la race en el caso
      // normal. Estas capas son defensa para casos donde la espera
      // expira por timeout (5s) o falla.
      if (onNeedRefreshFired) {
        Sentry.addBreadcrumb({
          category: 'version-manager',
          level: 'info',
          message: 'onNeedRefresh suppressed (already fired in this session)',
          data: { trigger: 'sw-need-refresh-dedup' },
        });
        return;
      }

      try {
        const recentFireTs = sessionStorage.getItem(
          VERSION_MODAL_RECENT_FIRE_KEY
        );
        if (recentFireTs) {
          const ageMs = Date.now() - parseInt(recentFireTs, 10);
          if (ageMs >= 0 && ageMs < VERSION_MODAL_RECENT_FIRE_TTL_MS) {
            Sentry.addBreadcrumb({
              category: 'version-manager',
              level: 'info',
              message: 'onNeedRefresh suppressed (recent fire) — self-heal',
              data: { ageMs, trigger: 'sw-need-refresh' },
            });
            // NO borramos el sentinel — Chrome puede disparar onNeedRefresh
            // OTRA VEZ en ms consecutivos (bug workbox#2526). Marcamos el
            // flag de módulo para cubrir también este caso por si el
            // segundo fire viene antes de leer sessionStorage.
            onNeedRefreshFired = true;

            // SELF-HEAL (fix del "modal por segunda vez"): llegar aquí
            // significa que el usuario consintió la actualización hace <30s
            // pero la race se perdió (SW v2 no activó antes del reload y v1
            // sirvió el bundle VIEJO). Antes solo suprimíamos y v2 quedaba
            // en `waiting` para siempre: a los >30s cualquier navegación
            // re-disparaba `waiting` → modal OTRA VEZ pidiendo lo que el
            // usuario ya aceptó. Ahora completamos su intención en
            // silencio: skipWaiting + espera de activación + UN reload.
            // Bucle acotado: si volviera a perderse la race, el sentinel
            // sigue <30s → reintento; al expirar el TTL, modal legítimo.
            void (async () => {
              try {
                const reg = await navigator.serviceWorker?.getRegistration();
                const waitingSW = reg?.waiting ?? null;
                await updateSW();
                if (waitingSW) {
                  await VersionManager.waitForSWActivation(waitingSW, 8000);
                }
                sessionStorage.setItem(
                  VERSION_MODAL_RECENT_FIRE_KEY,
                  String(Date.now())
                );
                window.location.reload();
              } catch (error) {
                Sentry.captureException(error);
              }
            })();
            return;
          }
          // TTL expirado: limpiamos el sentinel huérfano y seguimos.
          sessionStorage.removeItem(VERSION_MODAL_RECENT_FIRE_KEY);
        }
      } catch {
        // sessionStorage podría no estar disponible (modo privado estricto).
        // Si falla, dejamos que el flujo normal corra — peor caso es un
        // modal duplicado, no un crash.
      }

      // Marcamos que ya hemos procesado un `onNeedRefresh` legítimo en
      // esta sesión, para que el SEGUNDO evento `waiting` que Chrome
      // dispara no abra un modal extra.
      onNeedRefreshFired = true;

      // SW nuevo en estado `waiting`: hay una versión nueva desplegada. Si
      // todavía no hay sesión, nadie escucha el evento de abajo y no pasa
      // nada; la comprobación periódica de `useAppUpdate` la recogerá.
      Sentry.addBreadcrumb({
        category: 'version-manager',
        level: 'info',
        message: 'onNeedRefresh fired',
        data: {
          // Bundle version at fire time; reading from package.json import
          // would couple this file to versionManager internals — keep simple
          // and let versionManager's own breadcrumbs supply richer data.
          trigger: 'sw-need-refresh',
        },
      });
      // Ya no abre el aviso aquí: pide una comprobación a `useAppUpdate`, que
      // aplica las mismas guardas y la misma escalera que en la app. De paso
      // deja de salir con la versión VIEJA (no pasaba `targetVersion`) y de
      // perderse al arrancar (llegaba antes que el store).
      window.dispatchEvent(new Event(CHECK_UPDATE_EVENT));
    },
    onOfflineReady() {
      // Primera instalación lista para offline. No mostramos toast — el
      // soporte offline es transparente para el usuario en ROIS.
    },
    onRegisterError(error) {
      Sentry.captureException(error);
    },
  });

  // Exponer updateSW al versionManager para que su onContinue lo llame
  // antes del reload (skipWaiting si hay SW v2 en waiting). Se registra
  // siempre (no sólo en prod) porque el botón "Limpiar caché" del
  // DropDownUserMenu (visible en todos los entornos) y los helpers de
  // debug expuestos en MODE=development invocan el flujo manualmente.
  versionManager.setSWUpdater(updateSW);
}

// Language debug helper (solo en desarrollo)
if (import.meta.env.DEV) {
  import('./utils/languageDebugHelper');
}

// App nativa: recuperar sesión y borradores del almacenamiento duradero ANTES
// de montar React. Los loaders del router leen la sesión en el primer render,
// así que llegar tarde equivale a no haberlo hecho.
//
// Es asíncrono porque Preferences (SharedPreferences de Android) lo es. Se
// paga una espera de milisegundos UNA vez, en el arranque; a cambio, todas las
// lecturas posteriores siguen siendo síncronas y ningún consumidor cambia.
//
// En web ambas funciones son no-op y el render arranca igual de rápido.
import {
  bootstrapNativeStorage,
  installNativeStorageMirror,
  installBackgroundFlush,
  NATIVE_BOOTSTRAP_BUDGET_MS,
} from './offline/nativeDurableStorage';
import { notifyAppReady } from './utils/appUpdate';
import {
  beginBoot,
  markBoot,
  markRescued,
  lastMilestone,
  expiredSteps,
  withDeadline,
} from './utils/bootTrace';

// Traza del arranque. Va lo primero porque su trabajo es, además de apuntar
// hitos, DENUNCIAR el arranque anterior si se quedó a medias: un arranque
// colgado no puede contarlo —está colgado—, así que lo cuenta el siguiente.
beginBoot();

// 🔴 Lo PRIMERO de todo. Si el bundle no confirma que ha arrancado, el updater
// da por hecho que está roto y revierte al anterior. Se llama antes de montar
// React y sin esperar a ninguna petición: solo confirma que el JavaScript
// cargó, no que la aplicación funcione. En web es un no-op.
notifyAppReady();
markBoot('updater-notified');

// Pedir almacenamiento persistente cuanto antes. Sin esto el origen vive en
// modo best-effort y el navegador puede desalojar IndexedDB —donde está la cola
// de trabajo offline del operario— por falta de disco o por desuso.
//
// Aquí es oportunista: en una primera visita Chrome lo suele denegar, porque
// decide por heurísticas de uso. El intento que más cuenta es el que se hace al
// encolar trabajo (ver `offlineQueue.addToQueue`).
import { ensurePersistentStorage } from './offline/persistentStorage';
void ensurePersistentStorage('arranque de la app');

// Root element
import App from './App';
import { getSessionToRestoreOffline } from './utils/offlineSession';
import { setSessionStorageUser } from './utils/sessionStorage';

/**
 * Plazo del sondeo de reapertura sin cobertura. Ver la nota de los plazos en
 * `nativeDurableStorage`: mismo criterio.
 */
const SESSION_RESTORE_DEADLINE_MS = 5_000;

/**
 * Reapertura SIN COBERTURA: si esta pestaña no trae sesión y no se llega al
 * servidor, se restaura la copia guardada (si sigue dentro de su ventana de
 * 12 h). Es el caso del operario al que se le cierra la app en un corredor sin
 * cobertura — hasta ahora se quedaba fuera, porque el login necesita red.
 *
 * ── Por qué ANTES de renderizar, y no dentro de la app ────────────────────
 * `AuthProvider` está por encima de `AppApolloProvider` en el árbol y lee
 * `sessionStorage` al montarse. Restaurar más tarde (por ejemplo en el `init()`
 * de Apollo) no sirve de nada: su estado de React ya se creó sin sesión y el
 * router manda a `/login`. Comprobado con el build de producción — el módulo
 * hacía lo correcto y la app seguía yendo a login.
 *
 * ── Por qué el retraso es aceptable ───────────────────────────────────────
 * `getSessionToRestoreOffline()` sale enseguida en el caso corriente: si la
 * pestaña ya tiene sesión, o si no hay copia guardada, devuelve `null` SIN
 * tocar la red. Solo comprueba conectividad cuando de verdad hay una copia que
 * podría usarse, y ahí la respuesta es rápida en los dos extremos: sin red el
 * `fetch` falla de inmediato; con red, el health de beta contesta en ~0,2 s.
 */
async function arrancar() {
  // 1) App nativa: volcar sesión y borradores de Preferences a `sessionStorage`
  //    ANTES que nada. Va primero porque es la sesión REAL del dispositivo; la
  //    restauración offline de abajo se limita a comprobar si falta alguna.
  try {
    await bootstrapNativeStorage();
  } catch (err) {
    // Un fallo restaurando no puede impedir que la app arranque: en el peor
    // caso el operario ve la pantalla de login, que es el comportamiento de
    // siempre. El CUELGUE (que no lanza y por tanto no llega aquí) lo cubren
    // los plazos de dentro de `bootstrapNativeStorage`.
    Sentry.captureException(err);
  }
  // El espejo va ANTES del paso 2: si ahí se restaura una sesión, la escritura
  // se replica sola al almacén nativo. (El volcado a segundo plano lo engancha
  // `mountReact`, que corre en los dos caminos.)
  installNativeStorageMirror();
  markBoot('mirror-installed');

  // 2) Reapertura sin cobertura. Si el paso 1 ya dejó sesión, esta llamada
  //    devuelve `null` enseguida y sin tocar la red.
  //
  // Con PLAZO, igual que los pasos de arriba. Este sí sale a la red: su sondeo
  // está acotado a 2,5 s por `AbortController`, pero el tope no depende de que
  // esa garantía siga ahí mañana — es el último `await` antes de montar React.
  const offlineSession = await withDeadline(
    () => getSessionToRestoreOffline(),
    SESSION_RESTORE_DEADLINE_MS,
    null,
    'offline-session'
  );
  try {
    // 🔴 `!mounted`: si la red de seguridad ya montó la app mientras esto
    // esperaba, escribir ahora la sesión la pondría DETRÁS de una pantalla de
    // login ya pintada — o encima del operario que acaba de entrar en ella.
    if (offlineSession && !mounted) setSessionStorageUser(offlineSession);
  } catch (err) {
    // Escribir la sesión puede lanzar (cuota llena). Sin este guard, la
    // excepción rechazaba `arrancar()` entera, nadie la capturaba y el
    // operario pagaba los segundos enteros de la red de seguridad para acabar
    // en la misma pantalla.
    Sentry.captureException(err);
  }

  mountReact('boot');
}

/**
 * Monta React. Idempotente: la red de seguridad de abajo puede llamarlo a la
 * vez que el arranque normal, y montar dos veces sobre el mismo nodo pintaría
 * la app por duplicado.
 */
let mounted = false;
function mountReact(via: 'boot' | 'rescue'): void {
  if (mounted) return;
  mounted = true;

  // 🔴 El cableado del almacenamiento va aquí y no solo en `arrancar()`.
  //
  // Si monta la red de seguridad, `arrancar()` sigue colgada más arriba y esto
  // no se ha hecho: la app correría una jornada entera SIN replicar nada al
  // almacén duradero y sin volcar al pasar a segundo plano —que es el único
  // aviso que da Android antes de matar el proceso—. El operario perdería la
  // sesión y los borradores sin enterarse, que es peor que la pantalla blanca.
  // Las dos llamadas son idempotentes.
  //
  // Y dentro de un `try`: `mounted` ya es `true`, así que si esto lanzara y
  // no se llegara al `render()`, la red de seguridad tampoco volvería a
  // intentarlo. Sin cableado se pierde la persistencia; sin `render()` se
  // pierde la app entera.
  try {
    installNativeStorageMirror();
    installBackgroundFlush();
  } catch (err) {
    Sentry.captureException(err, { tags: { feature: 'boot', step: 'storage-wiring' } });
  }

  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );

  // Montar React NO cierra el arranque: el splash también es React. Lo cierra
  // `AppApolloProvider` cuando la app es usable (`completeBoot`). Si montó el
  // rescate, se apunta y la evidencia de dónde se quedó queda congelada.
  if (via === 'boot') markBoot('react-mounted');
  else markRescued();
}

/**
 * Red de seguridad del arranque.
 *
 * Los plazos de cada paso ya garantizan que se llega a `mountReact()` en los
 * modos de fallo conocidos. Esto cubre los que no lo son: un `await` nuevo que
 * alguien añada mañana sin tope, o un plugin que se cuelgue en un sitio que hoy
 * no miramos. El coste es un `setTimeout`; lo que evita es el fallo peor que
 * tiene esta app —pantalla blanca sin salida en una tablet en carretera— y que
 * además se repite en cada arranque, porque su causa está en disco.
 *
 * 🔴 El plazo se CALCULA a partir de los plazos reales, no se escribe a mano.
 * Con un número copiado bastaba subir un plazo de `bootstrapNativeStorage` para
 * que el rescate se adelantara a un arranque que iba a terminar solo — y un
 * rescate prematuro deja al operario sin cobertura en el login, que es una
 * pantalla que ahí no puede usar.
 *
 * El margen no es decorativo: cubre la evaluación de los módulos, el primer
 * render y el hecho de que un dispositivo cargado puede pasarse de todos los
 * plazos a la vez.
 */
const WATCHDOG_MARGIN_MS = 10_000;
const BOOT_WATCHDOG_MS =
  NATIVE_BOOTSTRAP_BUDGET_MS + SESSION_RESTORE_DEADLINE_MS + WATCHDOG_MARGIN_MS;

setTimeout(() => {
  if (mounted) return;
  console.error(
    `[boot] watchdog fired after ${BOOT_WATCHDOG_MS} ms — last milestone: ${lastMilestone()}`
  );
  Sentry.captureMessage('[boot] mounted by the watchdog', {
    level: 'error',
    tags: { feature: 'boot' },
    extra: {
      lastMilestone: lastMilestone(),
      // Qué pasos vencieron antes de llegar aquí: sin esto, el aviso dice que
      // algo se atascó pero no dónde, que es la mitad de la información.
      expired: expiredSteps(),
      watchdogMs: BOOT_WATCHDOG_MS,
    },
  });
  // Se monta igualmente: la app sin sesión restaurada lleva al login, que es
  // una pantalla con salida. Una pantalla blanca no la tiene.
  mountReact('rescue');
}, BOOT_WATCHDOG_MS);

void arrancar();
